import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FileText, ExternalLink, Search } from 'lucide-react';
import { BottomSheet, BottomSheetTextButton } from '@/components/mobile';

import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components//ui/button';
import { Input } from '@/components/ui/input';
import { IconButton } from '@/components/ui/custom/IconButton';

import { fetchForms } from '@/actions/forms';
import { Link, useNavigate } from 'react-router-dom';

// variant 'sheet' (phones): no trigger; the caller opens a bottom sheet with
// `open` / `onOpenChange` (the composer's "+" menu does).
const AttachFormButton = ({
	onInsert,
	disabled = false,
	variant = 'popover',
	open: openProp,
	onOpenChange,
}) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { forms, loading } = useSelector((state) => state.forms);
	const [openState, setOpenState] = useState(false);
	const open = openProp ?? openState;
	const setOpen = (next) => {
		if (openProp === undefined) setOpenState(next);
		onOpenChange?.(next);
	};
	const [query, setQuery] = useState('');
	const [highlightedIndex, setHighlightedIndex] = useState(-1);

	useEffect(() => {
		if (open && Array.isArray(forms) && forms.length === 0 && !loading) {
			dispatch(fetchForms());
		}
	}, [open, forms, loading, dispatch]);

	const sortedForms = Array.isArray(forms)
		? [...forms].sort((a, b) => {
				const titleA = (a.title || '').toString();
				const titleB = (b.title || '').toString();
				return titleA.localeCompare(titleB);
		  })
		: [];

	const filteredForms = React.useMemo(() => {
		const normalized = query.trim().toLowerCase();
		if (!normalized) return sortedForms;
		return sortedForms.filter((form) => {
			const title = (form.title || '').toString().toLowerCase();
			const description = (form.description || '')
				.toString()
				.toLowerCase();
			return (
				title.includes(normalized) || description.includes(normalized)
			);
		});
	}, [query, sortedForms]);

	useEffect(() => {
		// Reset search and highlight when popover opens/closes
		if (!open) {
			setQuery('');
			setHighlightedIndex(-1);
		}
	}, [open]);

	useEffect(() => {
		// Keep the highlighted option in view
		if (highlightedIndex < 0) return;
		const el = document.getElementById(
			`attach-form-option-${highlightedIndex}`
		);
		if (el && typeof el.scrollIntoView === 'function') {
			el.scrollIntoView({ block: 'nearest' });
		}
	}, [highlightedIndex]);

	const handleInputKeyDown = (e) => {
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			if (filteredForms.length === 0) return;
			setHighlightedIndex((prev) => {
				if (prev < 0) return 0;
				return Math.min(prev + 1, filteredForms.length - 1);
			});
			return;
		}
		if (e.key === 'ArrowUp') {
			e.preventDefault();
			if (filteredForms.length === 0) return;
			setHighlightedIndex((prev) => {
				if (prev <= 0) return 0;
				return prev - 1;
			});
			return;
		}
		if (e.key === 'Enter') {
			e.preventDefault();
			if (
				highlightedIndex >= 0 &&
				highlightedIndex < filteredForms.length
			) {
				handleSelect(filteredForms[highlightedIndex]);
			}
		}
	};

	const handleSelect = (form) => {
		if (!form || !form.code) return;
		if (onInsert) {
			onInsert(form.code);
		}
		setOpen(false);
	};

	if (variant === 'sheet') {
		return (
			<BottomSheet
				open={open}
				onOpenChange={setOpen}
				title="Attach a form"
				size="large"
				headerLeft={
					<BottomSheetTextButton onClick={() => setOpen(false)}>
						Cancel
					</BottomSheetTextButton>
				}
				headerRight={
					<BottomSheetTextButton
						onClick={() => {
							setOpen(false);
							navigate('/forms');
						}}
					>
						Forms
					</BottomSheetTextButton>
				}
			>
				{sortedForms.length === 0 && !loading ? (
					<p className="py-8 text-center text-[15px] text-muted-foreground">
						No forms yet.{' '}
						<Link to="/forms" className="text-primary underline-offset-2 hover:underline">
							Create one
						</Link>
					</p>
				) : (
					<div className="flex flex-col gap-3">
						<label className="flex h-11 items-center gap-2 rounded-[10px] bg-muted px-3">
							<Search className="size-4 shrink-0 text-muted-foreground" aria-hidden />
							<input
								type="search"
								value={query}
								onChange={(e) => setQuery(e.target.value)}
								placeholder="Search forms"
								aria-label="Search forms"
								className="min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground"
								autoComplete="off"
								autoCapitalize="none"
								enterKeyHint="search"
							/>
						</label>
						{filteredForms.length === 0 ? (
							<p className="py-6 text-center text-[15px] text-muted-foreground">
								No matching forms
							</p>
						) : (
							<div
								className="overflow-hidden rounded-xl border border-border/60"
								role="listbox"
								aria-label="Forms"
							>
								{filteredForms.map((form, index) => (
									<button
										key={form._id}
										type="button"
										role="option"
										aria-selected={false}
										disabled={disabled}
										onClick={() => handleSelect(form)}
										className={`flex min-h-14 w-full flex-col justify-center px-4 py-2.5 text-left active:bg-muted disabled:opacity-40 ${
											index > 0 ? 'border-t border-border/60' : ''
										}`}
									>
										<span className="truncate text-[17px] leading-snug">
											{form.title || 'Untitled'}
										</span>
										{form.description ? (
											<span className="line-clamp-2 break-words text-[13px] text-muted-foreground">
												{form.description}
											</span>
										) : null}
									</button>
								))}
							</div>
						)}
					</div>
				)}
			</BottomSheet>
		);
	}

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<span>
					<IconButton
						aria-label="attach form"
						disabled={disabled}
						toolTipTitle="Attach form"
					>
						<FileText className="size-4" />
						<span className="sr-only">Attach form</span>
					</IconButton>
				</span>
			</PopoverTrigger>
			<PopoverContent
				align="start"
				className="w-80 p-2 overflow-x-hidden"
			>
				{sortedForms.length === 0 && !loading ? (
					<p className="text-sm px-1 py-2">
						No forms found,{' '}
						<Link to="/forms" className="text-blue-500">
							create one here
						</Link>
					</p>
				) : (
					<div className="flex flex-col gap-2">
						<div className="flex items-center gap-1">
							<Input
								value={query}
								onChange={(e) => setQuery(e.target.value)}
								onKeyDown={handleInputKeyDown}
								placeholder="Search forms..."
								className="h-8 text-sm"
							/>
							<IconButton
								aria-label="go to forms"
								toolTipTitle="Go to Forms"
								className="size-8 shrink-0"
								onClick={() => {
									setOpen(false);
									navigate('/forms');
								}}
							>
								<ExternalLink className="size-3.5" />
							</IconButton>
						</div>
						{filteredForms.length === 0 ? (
							<p className="text-sm text-muted-foreground px-1 py-1">
								No matching forms
							</p>
						) : (
							<div
								className="flex flex-col gap-1 max-h-72 overflow-y-auto overflow-x-hidden"
								role="listbox"
								aria-label="Forms"
							>
								{filteredForms.map((form, index) => (
									<Button
										key={form._id}
										id={`attach-form-option-${index}`}
										variant="ghost"
										className={`justify-start h-auto py-2 px-2 w-full ${
											highlightedIndex === index
												? 'bg-accent'
												: ''
										}`}
										onMouseEnter={() =>
											setHighlightedIndex(index)
										}
										onClick={() => handleSelect(form)}
										role="option"
										aria-selected={
											highlightedIndex === index
										}
									>
										<div className="flex flex-col text-left min-w-0 overflow-hidden">
											<span className="text-sm font-semibold leading-tight truncate">
												{form.title || 'Untitled'}
											</span>
											{form.description ? (
												<span className="text-xs text-muted-foreground line-clamp-2 break-words">
													{form.description}
												</span>
											) : null}
										</div>
									</Button>
								))}
							</div>
						)}
					</div>
				)}
			</PopoverContent>
		</Popover>
	);
};

export default AttachFormButton;
